import { z } from "zod";

import { ManifestType } from "@/apiClient/models/ManifestType";
import { StorageTier } from "@/apiClient/models/StorageTier";
import { asArray, asBoolean } from "@/schemas/dataAssetSearchSchema";
import { stripDefaults } from "@/hooks/useDebouncedQueryParams";

// Coerce single values to arrays and drop anything that isn't a known
// manifest type (stale/hand-edited URLs shouldn't brick the page).
const asManifestTypeArray = z.preprocess(
  (val) =>
    (val == null ? [] : Array.isArray(val) ? val : [val]).filter(
      (item): item is ManifestType =>
        Object.values(ManifestType).includes(item as ManifestType)
    ),
  z.array(z.enum(ManifestType)).default([])
);

/**
 * URL search-param schema for the Storage Ops page. Captures the tier-change
 * form plus page view state so a pasted URL reproduces the exact screen.
 * Preview results are server data and intentionally stay out of the URL.
 */
export const storageOpsSearchSchema = z.object({
  manifestPublicIds: asArray,
  manifestTypes: asManifestTypeArray,
  realAssetDeploymentPublicIds: asArray,
  dataSetPublicIds: asArray,
  fileTypes: asArray,
  pathQuery: z.string().default(""),
  regex: asBoolean,
  dataAssetPublicIds: z.string().default(""),
  targetTier: z.preprocess(
    (val) =>
      Object.values(StorageTier).includes(val as StorageTier)
        ? val
        : undefined,
    z.enum(StorageTier).default(StorageTier.COOL)
  ),
  allowFees: asBoolean,
  advancedOpen: asBoolean,
  groupByManifestType: asBoolean,
});

export type StorageOpsSearch = z.infer<typeof storageOpsSearchSchema>;

/** The schema's defaults — single source of truth for URL stripping/resets. */
export const storageOpsSearchDefaults = storageOpsSearchSchema.parse({});

/** Drop default-valued params so clean visits keep clean URLs. */
export const stripStorageOpsSearchDefaults = (search: StorageOpsSearch) =>
  stripDefaults(search, storageOpsSearchDefaults);

export const TIER_OPTIONS: StorageTier[] = [
  StorageTier.HOT,
  StorageTier.COOL,
  StorageTier.COLD,
  StorageTier.ARCHIVE,
];

export const TIER_BADGE_CLASSES: Record<string, string> = {
  hot: "bg-rose-50 text-rose-700 border-rose-100",
  cool: "bg-blue-50 text-blue-300 border-blue-100",
  cold: "bg-slate-100 text-slate-600 border-slate-200",
  archive: "bg-amber-50 text-amber-700 border-amber-100",
};

export const ROW_STATUS_BADGE_CLASSES: Record<string, string> = {
  requested: "bg-slate-100 text-slate-600 border-slate-200",
  applying: "bg-blue-50 text-blue-300 border-blue-100",
  rehydrating: "bg-amber-50 text-amber-700 border-amber-100",
  applied: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed: "bg-rose-50 text-rose-700 border-rose-100",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export const JOB_STATUS_BADGE_CLASSES: Record<string, string> = {
  created: "bg-slate-100 text-slate-600 border-slate-200",
  queued: "bg-slate-100 text-slate-600 border-slate-200",
  running: "bg-blue-50 text-blue-300 border-blue-100",
  success: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failure: "bg-rose-50 text-rose-700 border-rose-100",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

/** Split a comma/whitespace-separated user input into trimmed, non-empty tokens. */
export const parseDelimitedList = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);

/** Rough human-readable age of an ISO timestamp, e.g. "3h 12m". */
export const formatAgeSince = (isoTimestamp?: string | null): string => {
  if (!isoTimestamp) {
    return "—";
  }

  const elapsedMs = Date.now() - new Date(isoTimestamp).getTime();
  if (Number.isNaN(elapsedMs) || elapsedMs < 0) {
    return "—";
  }

  const totalMinutes = Math.floor(elapsedMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};
